import _ from 'lodash';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { I18n } from 'react-redux-i18n-lite';
import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import CircularProgress from '@material-ui/core/CircularProgress';

import { AccentCard, Badge } from '@netexknowledge/smart-components';
import UserImage from '../../common/UserImage';
import CustomIcon from '../../common/CustomIcon';
import * as discussionActions from '../../../actions/discussionActions';
import * as accountActions from '../../../actions/accountActions';
import NoDiscussionMessages from './NoDiscussionMessages';
import DiscussionMessageList from './DiscussionMessagesList';
import NewMessageInput from './NewMessageInput';
import DiscussionPlaceholder from '../DiscussionPlaceholder';
import { isFuture, isPast } from '../../../utils/dateUtlis';
import { capitalize } from '../../../utils/stringUtils';
import { DISCUSSION_TYPE_META, DISCUSSION_TYPE_NEUTRAL } from '../DiscussionsList/discussionTypeMeta';
import { getDiscussionAccentColor, isDueSoon } from '../DiscussionsList/discussionStateColors';

import { ROLE } from '../../../const';

class DiscussionPage extends Component {
  static propTypes = {
    discussion: PropTypes.object.isRequired,
    messages: PropTypes.array.isRequired,
    user: PropTypes.object.isRequired,
    discussionActions: PropTypes.object.isRequired,
    accountActions: PropTypes.object.isRequired,
    routeParams: PropTypes.object.isRequired,
    fetchingMessages: PropTypes.bool.isRequired,
    fetchingDiscussion: PropTypes.bool.isRequired,
    isAppOnline: PropTypes.bool,
    role: PropTypes.string
  };

  componentWillUnmount() {
    const { discussionActions } = this.props;
    discussionActions.setSelectedDiscussion();
    discussionActions.setSelectedDiscussionMessages();
    discussionActions.setSelectedDiscussionMessagesOrder();
  }

  componentDidMount() {
    const { routeParams } = this.props;
    if (routeParams.idDiscussion) {
      this.fetchInitialData();
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { routeParams, isAppOnline, accountActions, user } = this.props;
    if (!isAppOnline) {
      accountActions.redirectToHomeOrClassroom(user._id);
    }
    if (!prevProps.routeParams.idDiscussion && routeParams.idDiscussion) {
      this.fetchInitialData();
    }
  }

  fetchInitialData = () => {
    const { discussionActions, routeParams } = this.props;
    discussionActions.getDiscussion(routeParams.idDiscussion);
    discussionActions.getDiscussionMessages(routeParams.idDiscussion);
    discussionActions.addNewVisitToDiscussion();
  };

  openDiscussionCloseModal = () => {
    const { discussionActions, discussion } = this.props;
    discussionActions.setCloseDiscussionRevealOpen();
    discussionActions.setCloseDiscussionRevealData(discussion);
  };

  openDiscussionRemoveModal = () => {
    const { discussionActions, discussion } = this.props;
    discussionActions.setRemoveDiscussionRevealOpen();
    discussionActions.setRemoveDiscussionRevealData(discussion);
  };

  renderTypeRow = () => {
    const { discussion } = this.props;
    const meta = DISCUSSION_TYPE_META.classroom;
    const isClosed = isPast(discussion.dueDate);

    return (
      <div className="discussion-type-row">
        <CustomIcon name={meta.icon} size={15} color={DISCUSSION_TYPE_NEUTRAL.iconColor} />
        <span className="discussion-type-label" style={{ color: DISCUSSION_TYPE_NEUTRAL.textColor }}>
          {I18n.t(meta.labelKey)}
        </span>
        {isClosed && (
          <Badge label={I18n.t('discussions.common.closedBadge')} background="#fee7e7" textColor="#a5323f" />
        )}
      </div>
    );
  };

  renderTopRow = () => {
    const { discussion } = this.props;
    const dueDateStyle = !isPast(discussion.dueDate) && isDueSoon(discussion.dueDate) ? 'due-soon' : '';

    return (
      <div className="top-row">
        <UserImage picture={discussion.author.picture} classes="no-border" size={38} />
        <span>
          <div className="teacher-info">{`${discussion.author.name} ${discussion.author.surname} `}</div>
          <div className="dates-content">
            <div className="date">{capitalize(dayjs(discussion.startDate).fromNow())}</div>
            <div className="date">·</div>
            <div className="date">
              {discussion.visits}{' '}
              {discussion.visits === 1 ? I18n.t('discussions.common.visit') : I18n.t('discussions.common.visits')}
            </div>
            <div className="date">·</div>
            <div className={`date ${dueDateStyle}`}>{capitalize(dayjs(discussion.dueDate).calendar())}</div>
          </div>
        </span>
      </div>
    );
  };

  isUrl = word => {
    const regexUrl = /^(http(s):\/\/.)[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)$/g;
    return regexUrl.test(word);
  };

  printUrl = link => {
    return (
      <a style={{ cursor: 'pointer' }} href={link} target="_blank">
        {`${link} `}
      </a>
    );
  };

  renderDiscussionDescription = messageContent => {
    return (
      <>
        {messageContent
          .split('\n')
          .map(line => line.split(' ').map(word => (this.isUrl(word) ? this.printUrl(word) : `${word} `)))
          .map((line, index) => (
            <div key={index}>{line}</div>
          ))}
      </>
    );
  };

  renderDiscussionBody = () => {
    const { discussion } = this.props;

    return (
      <div className="discussion-body">
        <span className="discussion-title">{discussion.title}</span>
        <span className="discussion-description">{this.renderDiscussionDescription(discussion.description)}</span>
      </div>
    );
  };

  isTeacher = () => this.props.role === ROLE.INSTRUCTOR;

  renderDiscussionActions = () => {
    const { discussion } = this.props;
    const canCloseDiscussion = !isPast(discussion.dueDate) && !isFuture(discussion.startDate);
    return (
      this.isTeacher() && (
        <div className="discussion-actions">
          {canCloseDiscussion && (
            <a className="clickable-action" onClick={this.openDiscussionCloseModal}>
              {I18n.t('discussions.common.options.closeDiscussion')}
            </a>
          )}
          {canCloseDiscussion && '·'}
          <a className="clickable-action" onClick={this.openDiscussionRemoveModal}>
            {I18n.t('discussions.common.options.delete')}
          </a>
        </div>
      )
    );
  };

  sendMessage = async messageText => {
    const { discussionActions } = this.props;
    await discussionActions.sendMessageToDiscussion(messageText);
  };

  renderNewMessageInput = () => {
    const { discussion } = this.props;
    const isClosed = isPast(discussion.dueDate);
    return (
      <NewMessageInput
        onSaveMessage={this.sendMessage}
        disabled={isClosed || isFuture(discussion.startDate)}
        closedMessage={isClosed ? I18n.t('discussions.common.closedMessage') : undefined}
      />
    );
  };

  renderDiscussionMessages() {
    const { messages, fetchingMessages, discussion } = this.props;

    if (messages.length === 0) {
      if (fetchingMessages) {
        return (
          <div className="loading-posts">
            <CircularProgress />
          </div>
        );
      }
      return <NoDiscussionMessages />;
    }

    return (
      <DiscussionMessageList
        messages={messages}
        discussionId={discussion.id}
        disableReplies={isPast(discussion.dueDate) || isFuture(discussion.startDate)}
      />
    );
  }

  renderDiscussionPlaceholder = () => {
    return <DiscussionPlaceholder />;
  };

  renderDiscussion = () => {
    const { discussion } = this.props;

    return (
      <div className="discussion-topic-wrapper">
        <AccentCard accentColor={getDiscussionAccentColor(discussion)} className="discussion-topic">
          {this.renderTypeRow()}
          {this.renderTopRow()}
          {this.renderDiscussionBody()}
          {this.isTeacher() ? this.renderDiscussionActions() : undefined}
          <div className="discussion-section-divider" />
          {this.renderNewMessageInput()}
          {this.renderDiscussionMessages()}
        </AccentCard>
      </div>
    );
  };

  render() {
    const { fetchingDiscussion, discussion } = this.props;

    return (
      <div className="discussion-topic-container">
        {fetchingDiscussion || _.isEmpty(discussion) ? this.renderDiscussionPlaceholder() : this.renderDiscussion()}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    discussion: state.discussions.selectedDiscussion.discussion,
    fetchingDiscussion: state.discussions.selectedDiscussion.fetching,
    messages: state.discussions.selectedDiscussion.messages.list,
    fetchingMessages: state.discussions.selectedDiscussion.messages.fetching,
    user: state.account.user,
    isAppOnline: state.connection.status.online,
    routeParams: state.router.params,
    role: state.classrooms.selectedClassroom.classroom.role
  };
}

function mapDispatchToProps(dispatch) {
  return {
    discussionActions: bindActionCreators(discussionActions, dispatch),
    accountActions: bindActionCreators(accountActions, dispatch)
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DiscussionPage);
